import React, { useEffect } from 'react';
import {
  Tune,
  ArrowBackIos,
  AddCircleOutline,
  Edit,
  UploadFile,
  CheckCircle,
  HighlightOff,
  EmojiEvents,
  Delete
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import { Header } from '../../components/header/header';
import { ButtonIconStyled } from '../devices/devicesStyles';
import { CustomFirmwareVersionList } from '../../templates/versions/customFirmwareFiles/customFirmwareVersionList';

import Strings from '../../i18n';
import {
  StyledHeaderContainer,
  StyledTitleHeader,
  TooltipStyled
} from '../../components/layout/main/mainStyles';
import {
  useDeleteCustomFirmwareVersionMutation,
  useEditCustomFirmwareVersionMutation,
  useGetCustomFirmwareVersionsMutation
} from '../../services/firmwareVersions/firmwareVersionsService';
import { LoadingRowsComponent } from '../../components/commons/LoadingRows/LoadingRows';
import { ICustomFirmwareItemData } from '../../types/firmwareVersions/firmwareVersionsInterface';
import { DeleteButtonWithoutText } from '../../components/commons/DeleteButtonWithoutText/deleteButtonWithoutText';

export const CustomVersionsView = () => {
  const { versionId } = useParams();
  const navigate = useNavigate();
  const [selectedVersions, setSelectedVersions] = React.useState<
    ICustomFirmwareItemData[]
  >([]);
  const [
    getFirmwareVersions,
    {
      data: firmwareVersions,
      isLoading: isLoadingFirmwareVersions,
      isSuccess: isSuccessFirmwareVersions
    }
  ] = useGetCustomFirmwareVersionsMutation();

  const [
    deleteFirmwareVersions,
    {
      isLoading: isLoadingDeleteFirmwareVersions,
      isSuccess: isSuccessDeleteFirmwareVersions
    }
  ] = useDeleteCustomFirmwareVersionMutation();

  const [
    editCustomFirmwareVersion,
    { isSuccess: successEdit, data: dataEdit }
  ] = useEditCustomFirmwareVersionMutation();

  useEffect(() => {
    if (versionId) {
      getFirmwareVersions(versionId);
    }
  }, [versionId]);

  const reloadTable = () => {
    if (versionId) {
      getFirmwareVersions(versionId);
    }
    setSelectedVersions([]);
  };

  const redirectToAddVersion = () => {
    navigate(`/firmwareVersions/${versionId}/customVersions/add`);
  };

  const redirectToEditVersion = () => {
    navigate(
      `/firmwareVersions/${versionId}/customVersions/${selectedVersions[0].id}/edit`
    );
  };

  const deleteMultipleVersions = () => {
    const ids = selectedVersions.map((version) => version.id);
    deleteFirmwareVersions(ids);
  };

  const activateDeactivateVersion = (method: string) => {
    const selectedVersion = selectedVersions[0];
    const payload = {
      id: selectedVersion.id,
      name: selectedVersion.name,
      isProduction: selectedVersion.isProduction,
      active: method === Strings.versions.activate
    };
    editCustomFirmwareVersion(payload);
  };

  const promoteToProduction = () => {
    const selectedVersion = selectedVersions[0];
    const payload = {
      id: selectedVersion.id,
      name: selectedVersion.name,
      isProduction: true,
      active: selectedVersion.active
    };
    editCustomFirmwareVersion(payload);
  };

  const redirectToUploadedFiles = () => {
    navigate(
      `/firmwareVersions/${versionId}/customVersions/${selectedVersions[0].id}/files`
    );
  };

  useEffect(() => {
    if (isSuccessDeleteFirmwareVersions) {
      reloadTable();
      toast.success(Strings.versions.successDeleteVersion);
    }
  }, [isSuccessDeleteFirmwareVersions]);

  useEffect(() => {
    if (successEdit) {
      reloadTable();
      toast.success(Strings.versions.statusChanged);
    }
  }, [successEdit]);
  return (
    <>
      <Header>
        <StyledHeaderContainer>
          <div className="row justify-content-between">
            <div className="col-4 text-left">
              <ButtonIconStyled
                size="small"
                onClick={() => navigate(`/firmwareVersions`)}
              >
                <ArrowBackIos />
              </ButtonIconStyled>
            </div>
            <div className="col-4 text-center">
              <StyledTitleHeader>
                {Strings.serverAccess.menu.customVersions}
              </StyledTitleHeader>
            </div>
            <div className="col-4 text-right">
              <Tooltip
                title={Strings.versions.addVersion}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled size="small" onClick={redirectToAddVersion}>
                  <AddCircleOutline />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip
                title={Strings.common.edit}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled
                  size="small"
                  onClick={redirectToEditVersion}
                  disabled={selectedVersions.length !== 1}
                >
                  <Edit />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip
                title={Strings.versions.uploadedFiles}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled
                  size="small"
                  onClick={redirectToUploadedFiles}
                  disabled={selectedVersions.length !== 1}
                >
                  <UploadFile />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip
                title={Strings.versions.activate}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled
                  size="small"
                  onClick={() =>
                    activateDeactivateVersion(Strings.versions.activate)
                  }
                  disabled={selectedVersions.length !== 1}
                >
                  <CheckCircle />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip
                title={Strings.versions.deactivate}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled
                  size="small"
                  onClick={() =>
                    activateDeactivateVersion(Strings.versions.deactivate)
                  }
                  disabled={selectedVersions.length !== 1}
                >
                  <HighlightOff />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip
                title={Strings.versions.promoteToProduction}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <ButtonIconStyled
                  size="small"
                  onClick={promoteToProduction}
                  disabled={selectedVersions.length !== 1}
                >
                  <EmojiEvents />
                </ButtonIconStyled>
              </Tooltip>
              <Tooltip
                title={Strings.common.delete}
                placement="top"
                arrow
                componentsProps={TooltipStyled}
              >
                <DeleteButtonWithoutText
                  onDelete={deleteMultipleVersions}
                  id={selectedVersions[0]?.id}
                  textConfirmation={Strings.versions.confirmDeleteVersion}
                  titleConfirmation={Strings.versions.deleteVersion}
                  disabled={selectedVersions.length === 0}
                />
              </Tooltip>

              <ButtonIconStyled size="small">
                <Tune />
              </ButtonIconStyled>
            </div>
          </div>
        </StyledHeaderContainer>
      </Header>
      {isLoadingFirmwareVersions && <LoadingRowsComponent />}
      {isSuccessFirmwareVersions && (
        <CustomFirmwareVersionList
          firmwareVersions={firmwareVersions ?? []}
          selectedVersions={selectedVersions}
          setSelectedVersions={setSelectedVersions}
        />
      )}
    </>
  );
};
